import { useMemo, useState } from "react";
import {
  ShieldCheck,
  ShieldAlert,
  ChevronRight,
  ChevronDown,
  Download,
  CheckCircle2,
  XCircle,
  CircleSlash,
  PlusCircle,
  FileDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadQAPdf } from "@/lib/api";
import { toast } from "sonner";

const STATUS = {
  PASS: { color: "#34D399", bg: "rgba(16,185,129,0.1)", border: "rgba(16,185,129,0.25)", icon: CheckCircle2, label: "PASS" },
  FAIL: { color: "#F87171", bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.25)", icon: XCircle, label: "FAIL" },
  MISSING: { color: "#FBBF24", bg: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.25)", icon: CircleSlash, label: "MISSING" },
  EXTRA: { color: "#60A5FA", bg: "rgba(59,130,246,0.1)", border: "rgba(59,130,246,0.25)", icon: PlusCircle, label: "EXTRA" },
  NO_CHECKS: { color: "#71717A", bg: "rgba(113,113,122,0.1)", border: "rgba(113,113,122,0.25)", icon: CircleSlash, label: "NO CHECKS" },
};

function Badge({ status }) {
  const s = STATUS[status] || STATUS.NO_CHECKS;
  const Icon = s.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm font-mono text-[10px] font-semibold"
      style={{ color: s.color, background: s.bg, border: `1px solid ${s.border}` }}
    >
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function toCSV(qa) {
  const rows = [["asset_id", "type", "geometry", "asset_status", "attribute", "design", "survey", "deviation", "allowed", "unit", "check_status"]];
  qa.result.assets.forEach((a) => {
    if (!a.checks.length) {
      rows.push([a.asset_id, a.type, a.geometry, a.status, "", "", "", "", "", "", a.note || ""]);
    } else {
      a.checks.forEach((c) => {
        rows.push([a.asset_id, a.type, a.geometry, a.status, c.field, c.design, c.survey, c.deviation ?? "", c.allowed, c.unit, c.status]);
      });
    }
  });
  return rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
}

export default function QAResult({ qa }) {
  const { result, design_name, survey_name, standard_name } = qa;
  const { summary, by_geometry, assets } = result;
  const byCategory = result.by_category || [];
  const multiStd = (summary.standards_used || []).length > 1;
  const [open, setOpen] = useState(() => new Set());
  const conforming = summary.verdict === "CONFORMING";

  const grouped = useMemo(() => {
    const g = {};
    assets.forEach((a) => (g[a.geometry] = g[a.geometry] || []).push(a));
    const order = ["Polygon", "Line", "Point"];
    return Object.keys(g)
      .sort((x, y) => (order.indexOf(x) + 9) - (order.indexOf(y) + 9))
      .map((k) => ({ geometry: k, items: g[k] }));
  }, [assets]);

  const toggle = (id) =>
    setOpen((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const exportCSV = () => {
    const blob = new Blob([toCSV(qa)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `QA_Report_${(qa.name || "report").replace(/[^a-z0-9]+/gi, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPDF = async () => {
    setPdfBusy(true);
    try {
      await downloadQAPdf(
        { design_id: qa.design_id, survey_id: qa.survey_id, standard_id: qa.standard_id, links: qa.links || [], name: qa.name, standard_map: qa.standard_map || null, type_overrides: qa.type_overrides || null },
        `AsConstructed_Change_Report_${(qa.name || "report").replace(/[^a-z0-9]+/gi, "_")}.pdf`
      );
      toast.success("PDF report downloaded");
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="qa-result">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="font-mono text-xs text-zinc-500 flex items-center gap-2 flex-wrap">
          <span className="px-2 py-0.5 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-300">DESIGN: {design_name}</span>
          <span className="px-2 py-0.5 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-300">SURVEY: {survey_name}</span>
          {multiStd ? (
            <span className="px-2 py-0.5 rounded-sm bg-purple-600/15 border border-purple-600/30 text-purple-400" data-testid="qa-multi-std" title={(summary.standards_used || []).join(", ")}>STD: Per-type ({summary.standards_used.length})</span>
          ) : (
            <span className="px-2 py-0.5 rounded-sm bg-blue-600/15 border border-blue-600/30 text-blue-400">STD: {standard_name}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={exportPDF}
            data-testid="qa-export-pdf"
            disabled={pdfBusy}
            className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white"
          >
            <FileDown className="w-4 h-4 mr-2" /> {pdfBusy ? "Generating…" : "Export PDF"}
          </Button>
          <Button
            onClick={exportCSV}
            data-testid="qa-export-csv"
            variant="outline"
            className="rounded-sm border-zinc-700 bg-transparent hover:bg-zinc-900 text-zinc-300"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* verdict + conformance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          className="md:col-span-1 border rounded-sm p-5 flex items-center gap-4"
          style={{ borderColor: conforming ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)", background: conforming ? "rgba(16,185,129,0.06)" : "rgba(239,68,68,0.06)" }}
          data-testid="qa-verdict"
        >
          {conforming ? (
            <ShieldCheck className="w-10 h-10 text-emerald-400" />
          ) : (
            <ShieldAlert className="w-10 h-10 text-red-400" />
          )}
          <div>
            <div className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Assessment</div>
            <div className="font-display text-2xl font-bold" style={{ color: conforming ? "#34D399" : "#F87171" }}>
              {summary.verdict}
            </div>
          </div>
        </div>

        <div className="md:col-span-2 border border-zinc-800 rounded-sm bg-[#101010] p-5">
          <div className="flex items-end justify-between mb-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Conformance Rate</span>
            <span className="font-display text-3xl font-bold text-zinc-100" data-testid="qa-conformance">
              {summary.conformance_pct}%
            </span>
          </div>
          <div className="h-2.5 w-full rounded-sm bg-zinc-800 overflow-hidden">
            <div
              className="h-full rounded-sm transition-all"
              style={{ width: `${summary.conformance_pct}%`, background: conforming ? "#34D399" : "#FBBF24" }}
            />
          </div>
          <div className="font-mono text-[11px] text-zinc-500 mt-2">
            {summary.assets_pass} of {summary.assessed} assessed assets within tolerance ·{" "}
            {summary.checks_pass}/{summary.total_checks} attribute checks passed
            {(summary.matched_manual > 0 || summary.matched_auto > 0) && (
              <> · {summary.matched_auto} auto-linked, {summary.matched_manual} manually linked</>
            )}
          </div>
        </div>
      </div>

      {/* metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Metric label="Pass" value={summary.assets_pass} color="#34D399" testid="qa-metric-pass" />
        <Metric label="Fail" value={summary.assets_fail} color="#F87171" testid="qa-metric-fail" />
        <Metric label="Missing" value={summary.assets_missing} color="#FBBF24" testid="qa-metric-missing" />
        <Metric label="Extra" value={summary.assets_extra} color="#60A5FA" testid="qa-metric-extra" />
        <Metric label="Checks Failed" value={summary.checks_fail} color="#F87171" testid="qa-metric-checks-failed" />
      </div>

      {/* by category (per-type tolerance mapping) */}
      {multiStd && byCategory.length > 0 && (
        <div className="border border-purple-600/30 rounded-sm overflow-hidden" data-testid="qa-by-category">
          <div className="bg-purple-600/[0.08] px-4 py-2.5 border-b border-purple-600/20 font-mono text-[11px] uppercase tracking-wider text-purple-300">
            Conformance by asset category (per-type tolerance mapping)
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-zinc-500 font-mono text-[11px] uppercase border-b border-zinc-800">
                <th className="text-left font-medium px-4 py-2">Category</th>
                <th className="text-left font-medium px-4 py-2">Standard applied</th>
                <th className="text-right font-medium px-4 py-2">Total</th>
                <th className="text-right font-medium px-4 py-2 text-emerald-400">Pass</th>
                <th className="text-right font-medium px-4 py-2 text-red-400">Fail</th>
                <th className="text-right font-medium px-4 py-2 text-amber-400">Missing</th>
                <th className="text-right font-medium px-4 py-2 text-blue-400">Extra</th>
              </tr>
            </thead>
            <tbody>
              {byCategory.map((c) => (
                <tr key={c.category} className="border-b border-zinc-900 font-mono text-xs" data-testid={`qa-cat-${c.category}`}>
                  <td className="px-4 py-2.5 text-zinc-200">{c.label}</td>
                  <td className="px-4 py-2.5 text-purple-300">{c.standard || "—"}</td>
                  <td className="px-4 py-2.5 text-right text-zinc-400">{c.total}</td>
                  <td className="px-4 py-2.5 text-right text-emerald-400">{c.pass}</td>
                  <td className="px-4 py-2.5 text-right text-red-400">{c.fail}</td>
                  <td className="px-4 py-2.5 text-right text-amber-400">{c.missing}</td>
                  <td className="px-4 py-2.5 text-right text-blue-400">{c.extra}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* by geometry */}
      <div className="border border-zinc-800 rounded-sm overflow-hidden">
        <div className="bg-[#101010] px-4 py-2.5 border-b border-zinc-800 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
          Conformance by geometry (iterative order: polygons → lines → points)
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 font-mono text-[11px] uppercase border-b border-zinc-800">
              <th className="text-left font-medium px-4 py-2">Geometry</th>
              <th className="text-right font-medium px-4 py-2">Total</th>
              <th className="text-right font-medium px-4 py-2 text-emerald-400">Pass</th>
              <th className="text-right font-medium px-4 py-2 text-red-400">Fail</th>
              <th className="text-right font-medium px-4 py-2 text-amber-400">Missing</th>
              <th className="text-right font-medium px-4 py-2 text-blue-400">Extra</th>
            </tr>
          </thead>
          <tbody>
            {by_geometry.map((g) => (
              <tr key={g.geometry} className="border-b border-zinc-900 font-mono text-xs">
                <td className="px-4 py-2.5 text-zinc-200">{g.geometry}</td>
                <td className="px-4 py-2.5 text-right text-zinc-400">{g.total}</td>
                <td className="px-4 py-2.5 text-right text-emerald-400">{g.pass}</td>
                <td className="px-4 py-2.5 text-right text-red-400">{g.fail}</td>
                <td className="px-4 py-2.5 text-right text-amber-400">{g.missing}</td>
                <td className="px-4 py-2.5 text-right text-blue-400">{g.extra}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* asset list */}
      <div className="space-y-5">
        {grouped.map((group) => (
          <div key={group.geometry}>
            <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> {group.geometry} ({group.items.length})
            </h3>
            <div className="space-y-2">
              {group.items.map((a) => {
                const isOpen = open.has(a.asset_id);
                const expandable = a.checks.length > 0;
                return (
                  <div key={a.asset_id} className="border border-zinc-800 rounded-sm bg-[#101010] overflow-hidden" data-testid={`qa-asset-${a.asset_id}`}>
                    <div
                      className={`flex items-center justify-between px-4 py-3 ${expandable ? "cursor-pointer hover:bg-zinc-900/50" : ""}`}
                      onClick={() => expandable && toggle(a.asset_id)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-4 text-zinc-600">
                          {expandable ? (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
                        </span>
                        <span className="font-mono text-xs text-zinc-200">{a.asset_id}</span>
                        {a.survey_id && a.survey_id !== a.asset_id && (
                          <span className="font-mono text-[10px] text-zinc-500">→ {a.survey_id}</span>
                        )}
                        <span className="font-mono text-xs text-zinc-500">{a.type}</span>
                        {multiStd && a.standard_name && (
                          <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-purple-600/15 border border-purple-600/30 text-purple-300" title={a.category}>{a.standard_name.split(" (")[0]}</span>
                        )}
                        {a.linked === "manual" && (
                          <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-blue-600/15 border border-blue-600/30 text-blue-400">
                            MANUAL LINK
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {a.note && <span className="font-mono text-[10px] text-zinc-500">{a.note}</span>}
                        {expandable && (
                          <span className="font-mono text-[10px] text-zinc-500">
                            {a.checks.filter((c) => c.status === "PASS").length}/{a.checks.length} ok
                          </span>
                        )}
                        <Badge status={a.status} />
                      </div>
                    </div>
                    {isOpen && expandable && (
                      <div className="border-t border-zinc-800 overflow-x-auto">
                        <table className="w-full font-mono text-xs">
                          <thead>
                            <tr className="text-zinc-600 text-[10px] uppercase border-b border-zinc-900">
                              <th className="text-left font-medium px-4 py-2">Attribute</th>
                              <th className="text-left font-medium px-4 py-2">Design</th>
                              <th className="text-left font-medium px-4 py-2">Survey</th>
                              <th className="text-right font-medium px-4 py-2">Deviation</th>
                              <th className="text-right font-medium px-4 py-2">Allowed</th>
                              <th className="text-right font-medium px-4 py-2">Result</th>
                            </tr>
                          </thead>
                          <tbody>
                            {a.checks.map((c, i) => (
                              <tr key={i} className="border-b border-zinc-900 last:border-0" style={{ background: c.status === "FAIL" ? "rgba(239,68,68,0.05)" : "transparent" }}>
                                <td className="px-4 py-2 text-zinc-400">{c.label}</td>
                                <td className="px-4 py-2 text-zinc-300">{c.design}</td>
                                <td className="px-4 py-2 text-zinc-100">{c.survey}</td>
                                <td className="px-4 py-2 text-right" style={{ color: c.status === "FAIL" ? "#F87171" : "#A1A1AA" }}>
                                  {c.deviation === null ? "—" : `${c.deviation} ${c.unit}`}
                                </td>
                                <td className="px-4 py-2 text-right text-zinc-500">
                                  {c.method === "exact" ? "exact" : `±${c.allowed} ${c.unit}`}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <Badge status={c.status} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value, color, testid }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-[#101010] p-4" data-testid={testid}>
      <div className="font-mono text-[11px] uppercase tracking-wider mb-2" style={{ color }}>
        {label}
      </div>
      <div className="font-display text-2xl font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
